import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import LegalShell, { Section, MailLink, SUPPORT_EMAIL } from "@/components/LegalShell";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ = [
  ["Getting started", [
    ["How does Astra personalise my horoscope?", "Astra uses your date of birth, plus your birth time and place if you add them, to work out your Sun, Moon and Rising signs. Each day it combines these with the real positions of the planets to write a reading just for you."],
    ["I don't know my birth time. Can I still use Astra?", "Yes. Your Sun sign and daily reading work from your birth date alone. Adding your time and place unlocks an accurate Rising sign and a more precise Moon sign. You can add them later in Sanctuary."],
    ["How do I change my birth details?", "Open Sanctuary (the profile tab), update your details and save. Your chart and future readings update straight away."],
  ]],
  ["Signing in", [
    ["Sign in with Apple isn't working", "Make sure you're signed in to iCloud on your iPhone (Settings → your name), then try again. If it still fails, close Astra fully, reopen it and try once more, or email us with a screenshot of the message."],
    ["I forgot my password", "Email us from the address you signed up with and we'll help you get back in. If you signed up with Apple, simply use Sign in with Apple again."],
  ]],
  ["Astra Interactive", [
    ["How many questions can I ask?", "Every account gets free questions to start. After that you can pick up a question pack, or earn free questions by inviting friends with your Share Astra link."],
    ["How do referral rewards work?", "Share your personal link from the Recommend a friend card. When a friend creates an account with it, you both receive 3 free Astra Interactive questions. Invite 5 friends and you also get a free month of Premium."],
  ]],
  ["Premium & purchases", [
    ["What does Premium include?", "Full Love, Money, Weekly and Monthly forecasts, Today's Sky details, and full readings for every sign."],
    ["How do I cancel or get a refund?", `Purchases made on astralogico.com are handled by Stripe. To cancel a subscription or ask about a refund, email ${SUPPORT_EMAIL} from your account email and we'll take care of it within one business day. Cancelling keeps Premium active until the end of the period you've paid for.`],
  ]],
  ["Notifications", [
    ["How do I turn daily reminders on or off?", "In Sanctuary you can switch email reminders and push notifications on or off and choose your reminder time. On iPhone you can also manage notifications in Settings → Notifications → Astra."],
    ["I'm not receiving notifications", "Check that notifications are allowed for Astra in iPhone Settings and that push is switched on in Sanctuary. Reminders arrive once a day at your chosen time."],
  ]],
  ["Privacy & your account", [
    ["How do I delete my account?", "Go to Sanctuary → Delete account. This permanently removes your profile, birth details, readings and chat history straight away."],
    ["Is my data shared?", "We never sell your data. See our Privacy Policy for the full details."],
  ]],
];

export default function Support() {
  return (
    <LegalShell kicker="Help" title="Support" testId="support-page">
      <p>Need a hand with Astra? Most answers are below. If you can't find what you need, we're happy to help.</p>

      <a
        data-testid="support-contact-btn"
        href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Astra support")}`}
        className="flex items-center justify-center gap-2 w-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3.5 rounded-full transition-colors"
      >
        <Mail size={17} /> Email support
      </a>
      <p className="text-sm text-[#A7B0C0] -mt-3 text-center">We reply within 1–2 business days.</p>

      {FAQ.map(([group, items], gi) => (
        <Section key={group} title={group}>
          <Accordion type="single" collapsible className="border-t border-[#D4AF37]/15">
            {items.map(([q, a], qi) => (
              <AccordionItem key={q} value={`${gi}-${qi}`} className="border-b border-[#D4AF37]/15">
                <AccordionTrigger data-testid={`faq-q-${gi}-${qi}`} className="text-left text-starlight hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-[#D6D0C5] text-base leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      ))}

      <Section title="Contact us">
        <p>Email <MailLink subject="Astra support" />. Please include the email on your account and, if something isn't working, a screenshot. It helps us fix it faster.</p>
        <p className="text-sm text-[#A7B0C0]">Astra readings are for entertainment and reflection, and aren't a substitute for professional advice.</p>
        <p className="text-sm"><Link to="/privacy" data-testid="support-privacy-link" className="gold-text underline underline-offset-4">Privacy Policy</Link></p>
      </Section>
    </LegalShell>
  );
}
